import { useEffect, useState } from 'react';
import { ListChecks, Play } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { muze } from '../lib/moduly';
import TabBar from '../components/layout/TabBar';
import ModalShell from '../components/ui/ModalShell';
import { REZERVA_LISTY } from '../components/MobilniLista';
import { SKLO_SVETLE } from '../lib/uiTokens';
import MereniPrehled from '../components/checklisty/MereniPrehled';
import ChecklistySeznam from '../components/checklisty/ChecklistySeznam';
import MericiBody from '../components/checklisty/MericiBody';
import SablonyZalozka from '../components/checklisty/SablonyZalozka';
import { SECTION_COLOR, SECTION_BG, hlavicky } from '../components/checklisty/ui';

/**
 * Modul Checklisty — podle normy OS (2026-09-30, návrh „Checklisty podle normy").
 *
 * Záložky (klíče přístupů zůstaly):
 *  - prehled   → Přehled: měření a odchylky pro vedení (MereniPrehled)
 *  - historie  → Checklisty: všechny vyplněné a rozpracované listy (sloučený
 *                dřívější Přehled a Historie), seskupené podle směny
 *  - parametry → Měřicí body (i měřidla energií a body napojené na kanály
 *                uzlu pobočky; spotřeba a náklady jsou v KPI Dashboardu)
 *  - sablony   → Šablony
 * Seznamové záložky: ModulLayout (seznam · detail · třetí sloupec), úpravy
 * v inspektoru, spuštění checklistu a nová šablona v modalu. Na mobilu
 * připnuté k výřezu, horní ovládání ve skle.
 */
export default function Checklisty({ user, initialTab = null, navParams = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile] = useState(() => window.innerWidth < 768);

    const povoleneTaby = ['prehled', 'historie', 'parametry', 'sablony'].filter(id => muze(user, `checklisty.${id}`));
    // Záložka z adresy (#/checklisty?tab=historie) — odkaz z notifikace, proklik
    const [tab, setTab] = useState(() => (initialTab && povoleneTaby.includes(initialTab) ? initialTab : povoleneTaby[0] ?? 'prehled'));
    useEffect(() => { if (initialTab && povoleneTaby.includes(initialTab)) setTab(initialTab); }, [initialTab]); // eslint-disable-line
    const [spustit, setSpustit] = useState(false);
    const [sablony, setSablony] = useState([]);
    const [otevrit, setOtevrit] = useState(null);
    const [obnova, setObnova] = useState(0);
    // Měřicí bod z adresy (#/checklisty?tab=parametry&bod=2), např. z notifikace
    const [otevritBod, setOtevritBod] = useState(navParams?.bod ?? null);
    useEffect(() => { if (navParams?.bod) setOtevritBod(navParams.bod); }, [navParams?.bod]);

    useEffect(() => {
        if (!spustit) return;
        fetch('/api/checklisty/sablony', { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : []))
            .then(s => setSablony((Array.isArray(s) ? s : []).filter(x => x.stav === 'aktivni'))).catch(() => {});
    }, [spustit]);

    const spustitSablonu = async (id) => {
        const res = await fetch('/api/checklisty', { method: 'POST', headers: hlavicky(), body: JSON.stringify({ sablona_id: id }) });
        if (!res.ok) return;
        const c = await res.json();
        setSpustit(false);
        setObnova(o => o + 1);
        setOtevrit(c.id);
        if (povoleneTaby.includes('historie')) setTab('historie');
    };

    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    const inputStyle = {
        width: '100%', height: 32, borderRadius: 8, padding: '0 10px', outline: 'none', fontSize: 13,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
    // Seznamové záložky: na mobilu připnuté k výřezu (norma §1b), na desktopu na výšku obsahu
    const pripnuta = isMobile
        ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
        : { height: '100%', minHeight: 0 };
    const rolujici = isMobile
        ? { ...pripnuta, overflowY: 'auto', padding: `12px 14px ${REZERVA_LISTY}` }
        : { height: '100%', minHeight: 0, overflowY: 'auto', paddingBottom: 24 };
    const spolecne = { isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole: isMobile ? REZERVA_LISTY : null };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                {!isMobile && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 7, background: SECTION_BG }}>
                            <ListChecks size={20} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Kontroly a měření</h1>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Kontroly na směnách, teploty, energie a odchylky</p>
                        </div>
                    </div>
                )}
                <TabBar modul="checklisty" user={user} aktivni={tab} onZmena={setTab} barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            <div style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16, display: 'flex', flexDirection: 'column' }}>
                {tab === 'prehled' && (
                    <div style={rolujici}>
                        <MereniPrehled isDark={isDark} isMobile={isMobile} barva={SECTION_COLOR}
                            onNastavit={povoleneTaby.includes('parametry') ? () => setTab('parametry') : null} />
                    </div>
                )}
                {tab === 'historie' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <ChecklistySeznam {...spolecne} obnova={obnova} otevritId={otevrit} onSpustit={() => setSpustit(true)} />
                    </div>
                )}
                {tab === 'parametry' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <MericiBody {...spolecne} upravuje={muze(user, 'checklisty.parametry', 'upravuje')} otevritId={otevritBod} />
                    </div>
                )}
                {tab === 'sablony' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <SablonyZalozka {...spolecne} upravuje={muze(user, 'checklisty.sablony', 'upravuje')} />
                    </div>
                )}
            </div>

            {spustit && (
                <ModalShell onClose={() => setSpustit(false)} isDark={isDark} barva={SECTION_COLOR} ikona={Play} nadpis="Spustit checklist" sirka={460}>
                    <div style={{ padding: '8px 12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {sablony.length === 0 && <p className="text-sm text-center" style={{ color: mutedColor, padding: 16 }}>Žádné aktivní šablony.</p>}
                        {sablony.map(s => (
                            <button key={s.id} type="button" onClick={() => spustitSablonu(s.id)} className="text-left"
                                style={{ padding: '11px 14px', borderRadius: 10, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                                <b style={{ fontSize: 14 }}>{s.nazev}</b>
                                <span style={{ display: 'block', fontSize: 12, color: mutedColor, marginTop: 2 }}>
                                    {s.polozky_count} položek{(s.pouziti || []).length ? ` · ${[...new Set(s.pouziti.map(x => x.kod))].join(', ')}` : ''}
                                </span>
                            </button>
                        ))}
                    </div>
                </ModalShell>
            )}
        </div>
    );
}
